import React, { useEffect, useState } from "react";
import { Button, TextField, Autocomplete } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DatePickerWithTimeComponent from "../DatePickerWithTimeComponent";
import {
  addDoc,
  collection,
  serverTimestamp,
  getDocs,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import { showCustomToast } from "../Misc/CustomToast";
import Chip from "@mui/material/Chip";

function AddPsychoEdNote(props) {
  const { currentUser } = useAuth();

  const [noteText, setNoteText] = useState("");
  const [subject, setSubject] = useState("");
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [effectiveDateTime, setEffectiveDateTime] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const draftKey = `draft:psychoEdNote:${currentUser?.uid || "anon"}:${
    props.clientId || "unknown"
  }`;
  const draftDelay = 800;
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const handleDateTimeChange = (event) => {
    setEffectiveDateTime(event);
  };

  const handleAdd = async (note, clientID, effectiveDateTime) => {
    if (!subject) {
      showCustomToast("Subject is required.", "error");
      return;
    }
    const noteData = {
      noteType: "psychoEdNote",
      noteContent: note,
      clientId: clientID || null,
      createdAt: serverTimestamp(),
      effectiveDateTime,
      doneBy: currentUser?.displayName || currentUser?.email || "Unknown",
      clientFirstName: props.firstName || null,
      clientLastName: props.lastName || null,
      clientProfilePicture: props.profilePicture || "",
      subject,
    };

    if (noteText && effectiveDateTime) {
      setIsLoading(true);
      try {
        await addDoc(collection(db, "notes"), noteData);
        // Persist subject in a simple collection for autocomplete
        try {
          await addDoc(collection(db, "psychoEdSubjects"), {
            subject,
            createdAt: serverTimestamp(),
            createdBy: currentUser?.uid || null,
          });
        } catch (e) {
          console.warn("Subject save failed:", e);
        }
        showCustomToast("Psycho-Ed note successfully added!", "success");
        setIsLoading(false);
        try {
          localStorage.removeItem(draftKey);
        } catch {}
        props.closemodal();
      } catch (error) {
        console.error("error adding note :", error);
        showCustomToast("Problem when adding note", "error");
        setIsLoading(false);
      }
    } else {
      showCustomToast(
        "Some mandatory fields are empty. Please check.",
        "error"
      );
    }
  };

  const handleSubmit = () => {
    handleAdd(noteText, props.clientId, effectiveDateTime);
  };

  const handleChange = (event) => {
    setNoteText(event.target.value);
  };

  // Restore draft on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.subject) setSubject(parsed.subject);
        if (parsed?.text) setNoteText(parsed.text);
        if (parsed?.date) {
          const d = new Date(parsed.date);
          if (!isNaN(d)) setEffectiveDateTime(d);
        }
      }
    } catch (e) {
      console.warn("PsychoEd draft load error:", e);
    }
    // Fetch past subjects for autocomplete
    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, "psychoEdSubjects"), orderBy("subject", "asc"))
        );
        const options = [];
        snap.forEach((d) => {
          const s = d.data()?.subject;
          if (s) options.push(s);
        });
        setSubjectOptions(Array.from(new Set(options)));
      } catch (e) {
        console.warn("PsychoEd subjects fetch error:", e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autosave draft with debounce
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        localStorage.setItem(
          draftKey,
          JSON.stringify({
            subject,
            text: noteText,
            date: effectiveDateTime?.toISOString?.() || null,
          })
        );
        setIsSavingDraft(false);
      } catch (e) {
        console.warn("PsychoEd draft save error:", e);
      }
    }, draftDelay);
    setIsSavingDraft(true);
    return () => clearTimeout(t);
  }, [noteText, effectiveDateTime, subject, draftKey]);

  return (
    <div style={{ width: "auto", height: "100%" }}>
      <div className="line1-modal-percent">
        <div className="modal-title" style={{ paddingLeft: "10px" }}>
          Add Psycho-Ed Note
        </div>
        <CloseIcon
          style={{
            marginRight: "16px",
            color: "rgb(214, 213, 213)",
            cursor: "pointer",
          }}
          onClick={props.closemodal}
        />
      </div>

      <div
        style={{
          width: "100%",
          marginTop: "0px",
          height: "75%",
          overflowY: "scroll",
          overflowX: "hidden",
        }}
      >
        <div className="line2-column-modal-bis">
          <div
            className="label-textfield"
            style={{ paddingLeft: "15px", paddingBottom: "5px" }}
          >
            Subject (required)
          </div>
          <div
            style={{
              paddingLeft: "15px",
              paddingRight: "15px",
              marginBottom: "12px",
              width: "100%",
            }}
          >
            <Autocomplete
              freeSolo
              options={subjectOptions}
              value={subject}
              onInputChange={(_, val) => setSubject(val)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Type or select a subject"
                  size="small"
                  sx={{
                    backgroundColor: "#f2eeef",
                    borderRadius: "5px",
                    width: "100%",
                  }}
                />
              )}
              fullWidth
            />
          </div>
          <div
            className="label-textfield"
            style={{ paddingLeft: "15px", paddingBottom: "5px" }}
          >
            Note content
          </div>
          <TextField
            name="psychoEdNote"
            value={noteText}
            onChange={handleChange}
            style={{
              width: "95%",
              borderRadius: "5px",
              backgroundColor: "#f2eeef",
            }}
            placeholder="Type Psycho-Ed note here"
            multiline
            rows={12}
          />
        </div>
        <div
          className="line3-modal-nopad"
          style={{
            paddingLeft: "30px",
            paddingTop: "10px",
            marginBottom: "20px",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <DatePickerWithTimeComponent
            title="Effective date and time"
            value={effectiveDateTime}
            onChange={handleDateTimeChange}
          />
        </div>
      </div>
      <div
        className="line4-modal-percent"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 10,
        }}
      >
        <Chip
          label={
            isSavingDraft
              ? "Saving draft..."
              : noteText
              ? "Draft saved"
              : "No draft"
          }
          size="small"
          sx={{
            backgroundColor: isSavingDraft
              ? "#f9a825"
              : noteText
              ? "#2e7d32"
              : "#607d8b",
            color: "#fff",
            fontWeight: 700,
          }}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            try {
              localStorage.removeItem(draftKey);
            } catch {}
            setNoteText("");
            setEffectiveDateTime(new Date());
          }}
        >
          Clear draft
        </Button>
        <Button
          variant="contained"
          style={{ backgroundColor: "white", color: "black" }}
          onClick={props.closemodal}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          style={
            isLoading
              ? { backgroundColor: "red", color: "white" }
              : { backgroundColor: "#1565C0", color: "white" }
          }
          disabled={isLoading}
          onClick={handleSubmit}
        >
          {isLoading ? "ADDING NOTE..." : "ADD NOTE"}
        </Button>
      </div>
    </div>
  );
}

export default AddPsychoEdNote;
